/**
 * Scout — the floating in-platform help assistant.
 *
 * A launcher bubble fixed to the bottom-right of every tenant page; opens
 * a compact chat panel backed by /api/companies/:id/assistant/*. Scout is
 * NOT an agent — it runs in-process on the platform with read-only,
 * tenant-scoped diagnostic tools, so this widget deliberately does not
 * reuse the workspace ChatSurface (no sessions list, files, or @mentions).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
// `History` is aliased: the bare name collides with the DOM History type and
// silently resolves to it, which fails as a JSX component rather than as a
// missing import.
import { HelpCircle, History as HistoryIcon, LifeBuoy, Loader2, Plus, Send, Sparkles, SquarePen, X, Check, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DEFAULT_PANEL_FRAME,
  clampFrame,
  loadPanelFrame,
  resizeFrame,
  savePanelFrame,
  type PanelFrame,
} from "@/lib/floating-panel";
import { UI_GUIDES, GUIDE_ROLE_LEVEL, GUIDE_SECTIONS, type GuideRole } from "@proclaw/shared";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { workflowsApi } from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import { ChatToolbarTooltip } from "@/components/chat/chat-toolbar-tooltip";
import { SupportPanel } from "./support-panel";
import {
  assistantApi,
  type AssistantPageContext,
  type AssistantMessage,
  type AssistantSessionSummary,
} from "@/api/assistant";
import { auditorChatApi, type EvalProposal } from "@/api/evals";
import { ProposalCard } from "./proposal-card";

const ASSISTANT_NAME = "Scout";
const AUDITOR_NAME = "Auditor";

const TOOL_LABELS: Record<string, string> = {
  search_help: "Searching help articles",
  read_help_article: "Reading help article",
  list_agents: "Looking up your agents",
  get_agent_health: "Checking agent health",
  list_workflows: "Looking up workflows",
  list_workflow_runs: "Checking workflow runs",
  get_workflow_run: "Inspecting the run timeline",
  list_mcp_servers: "Checking MCP servers",
  check_notification_channels: "Checking your notification options",
  create_support_ticket: "Logging your support request",
  offer_ui_guide: "Preparing a walkthrough",
};

interface ToolStep {
  id: string;
  name: string;
  status: "running" | "done" | "error";
}

interface GuideOffer {
  guideId: string;
  title: string;
}

interface LocalMessage {
  role: "user" | "assistant";
  content: string;
  toolSteps?: ToolStep[];
  guideOffers?: GuideOffer[];
}

const UUID_RE = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

function pageContextFromPath(pathname: string, search: string): AssistantPageContext {
  const ctx: AssistantPageContext = { path: pathname };
  const agent = pathname.match(new RegExp(`/agents/(${UUID_RE})`));
  if (agent) ctx.agentId = agent[1];
  // The workspace (chat, Tasks, Workflows) and the workflow pages carry the
  // agent as ?agentId=, not in the path; without this Scout never knew which
  // agent the sidebar and chat were on, so "my last run" was unscoped.
  if (!ctx.agentId) {
    const fromQuery = new URLSearchParams(search).get("agentId");
    if (fromQuery && new RegExp(`^${UUID_RE}$`).test(fromQuery)) ctx.agentId = fromQuery;
  }
  const workflow = pathname.match(new RegExp(`/workflows/(${UUID_RE})`));
  if (workflow) ctx.workflowId = workflow[1];
  const run = pathname.match(new RegExp(`/runs/(${UUID_RE})`));
  if (run) ctx.runId = run[1];
  return ctx;
}

function ToolStepRow({ step }: { step: ToolStep }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-text-muted">
      {step.status === "running" ? (
        <Loader2 size={12} className="animate-spin text-accent" />
      ) : step.status === "error" ? (
        <Wrench size={12} className="text-danger" />
      ) : (
        <Check size={12} className="text-accent" />
      )}
      <span>{TOOL_LABELS[step.name] ?? step.name}</span>
    </div>
  );
}

const SUGGESTIONS = [
  "How do I build a workflow?",
  "Why did my last workflow run fail?",
  "Is one of my agents having problems?",
  "What can I do on this platform?",
];

const AUDITOR_SUGGESTIONS = [
  "Which workflows are failing most often?",
  "What is our most expensive agent, and why?",
  "Could any of our workflows run on a cheaper model?",
  "Which agents carry the heaviest prompt prefix?",
];

export function ScoutWidget() {
  const { tenantId, isLoading: tenantLoading, isTenantAdmin, isAgentAdmin, isInstanceAdmin } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"chat" | "support">("chat");
  /**
   * Which agent is behind the chat. Shared shell, two different agents —
   * Scout answers how-do-I for anyone; the Auditor reads cross-agent cost and
   * config and is platform-admin only. Histories are stored separately, so
   * switching swaps the whole conversation rather than continuing one.
   */
  const [mode, setMode] = useState<"scout" | "auditor">("scout");
  const [proposals, setProposals] = useState<EvalProposal[]>([]);
  const [supportTicketId, setSupportTicketId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // ── Resize ────────────────────────────────────────────────────────────
  // Resize only — the panel stays anchored bottom-right. Dragging it around
  // was tried and removed: it added a grab cursor over the whole header and a
  // way to misplace the panel, to solve a problem nobody had. Size is the
  // thing people actually want to change.
  //
  // Clamping lives in lib/floating-panel so it stays unit-tested; this is just
  // the pointer plumbing. Pointer events rather than mouse so a trackpad or
  // touch drag works.
  const viewport = () => ({ width: window.innerWidth, height: window.innerHeight });
  const [frame, setFrame] = useState<PanelFrame>(DEFAULT_PANEL_FRAME);
  useEffect(() => {
    setFrame(loadPanelFrame(viewport()));
  }, []);
  // Re-clamp when the window changes, so a panel sized on a large monitor
  // still fits on a small one.
  useEffect(() => {
    const onResize = () => setFrame((f) => clampFrame(f, viewport()));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const startResize = useCallback((e: React.PointerEvent) => {
    const el = e.currentTarget as HTMLElement;
    const pointerId = e.pointerId;
    el.setPointerCapture(pointerId);
    let last = { x: e.clientX, y: e.clientY };
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - last.x;
      const dy = ev.clientY - last.y;
      last = { x: ev.clientX, y: ev.clientY };
      setFrame((f) => resizeFrame(f, dx, dy, viewport()));
    };
    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
      // MUST release: a captured pointer keeps routing events to this element
      // and leaves the resize cursor stuck across the whole page until the
      // next click. The first cut set capture and never released it.
      if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
      // Persist on release, not per move — one write instead of hundreds.
      setFrame((f) => {
        savePanelFrame(f);
        return f;
      });
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    e.preventDefault();
  }, []);

  // ── Recent conversations ──────────────────────────────────────────────
  // The counterpart to starting fresh on open: without somewhere to go back
  // to, resetting would just lose the user's last conversation.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState<AssistantSessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const openHistory = useCallback(() => {
    if (!tenantId) return;
    setHistoryOpen((was) => {
      // Fetch on each open rather than caching: the list changes as the user
      // talks, and a stale picker is worse than a brief spinner.
      if (!was) {
        assistantApi
          .listSessions(tenantId)
          .then((res) => setSessions(res.sessions))
          .catch(() => setSessions([]));
      }
      return !was;
    });
  }, [tenantId]);

  const openSession = useCallback(
    (sessionId: string) => {
      if (!tenantId) return;
      setHistoryOpen(false);
      setActiveSessionId(sessionId);
      assistantApi
        .getSession(tenantId, sessionId)
        .then((res) => {
          setMessages(
            res.messages.map((m: AssistantMessage) => ({
              role: m.role,
              content: m.content,
              toolSteps: m.metadata?.toolSteps?.map((s) => ({
                id: s.id,
                name: s.name,
                status: (s.status as ToolStep["status"]) ?? "done",
              })),
              guideOffers: m.metadata?.guideOffers,
            })),
          );
        })
        .catch(() => setError("Could not open that conversation."));
    },
    [tenantId],
  );

  const startNewConversation = useCallback(() => {
    if (!tenantId) return;
    setHistoryOpen(false);
    assistantApi
      .resetSession(tenantId)
      .then(() => {
        setActiveSessionId(null);
        setMessages([]);
        setError(null);
      })
      .catch(() => setError("Could not start a new conversation."));
  }, [tenantId]);
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [streamSteps, setStreamSteps] = useState<ToolStep[]>([]);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** Set when the widget was opened from a specific eval finding. */
  const focusReportIdRef = useRef<string | null>(null);

  // Opening from a report finding: ?auditor=<reportId> puts the widget into
  // Auditor mode already scoped, so the user doesn't have to re-describe what
  // they're looking at.
  useEffect(() => {
    const reportId = searchParams.get("auditor");
    if (!reportId || !isInstanceAdmin) return;
    focusReportIdRef.current = reportId;
    setMode("auditor");
    setView("chat");
    setOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("auditor");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isInstanceAdmin]);

  // Bell notifications deep-link with ?supportTicket=<id> — open the panel
  // straight onto that ticket, then strip the param.
  useEffect(() => {
    const ticketId = searchParams.get("supportTicket");
    if (!ticketId) return;
    setSupportTicketId(ticketId);
    setView("support");
    setOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("supportTicket");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load the Auditor's own history when switched into that mode. Kept in a
  // separate effect from Scout's because the two conversations are distinct
  // records, not two views of one thread.
  useEffect(() => {
    if (!open || mode !== "auditor" || !tenantId) return;
    let cancelled = false;
    setLoaded(false);
    auditorChatApi
      .getSession(tenantId)
      .then((res) => {
        if (cancelled) return;
        setMessages(
          res.messages.map((m) => ({
            role: m.role,
            content: m.content,
            toolSteps: m.metadata?.toolSteps?.map((s) => ({
              id: s.id,
              name: s.name,
              status: (s.status as ToolStep["status"]) ?? "done",
            })),
          })),
        );
        setProposals(res.proposals);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the evaluation conversation.");
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode, tenantId]);

  // Lazy-load the first time the panel opens.
  //
  // Once per browser session Scout starts a NEW conversation instead of
  // resuming the last one: opening the widget used to replay whatever was on
  // screen days ago, which reads as "it didn't reset" rather than "here is
  // your history". Past conversations are reachable from the history picker,
  // which is what makes starting clean safe rather than lossy.
  //
  // sessionStorage, not localStorage — "a new session" means a new tab or a
  // reopened browser, not a new day in the same tab. The reset creates an
  // empty session row; the history endpoint filters those out, so an opened-
  // but-unused widget never litters the list.
  useEffect(() => {
    if (!open || loaded || !tenantId || mode !== "scout") return;
    const freshKey = `proclaw.scout.fresh.${tenantId}`;
    const needsFresh = (() => {
      try {
        if (sessionStorage.getItem(freshKey)) return false;
        sessionStorage.setItem(freshKey, "1");
        return true;
      } catch {
        // Storage unavailable — resume rather than resetting on every open,
        // which would strand the user's conversation behind the picker.
        return false;
      }
    })();
    const load = needsFresh
      ? assistantApi.resetSession(tenantId).then(() => ({ messages: [] as AssistantMessage[] }))
      : assistantApi.getSession(tenantId);
    load
      .then((res) => {
        setMessages(
          res.messages.map((m: AssistantMessage) => ({
            role: m.role,
            content: m.content,
            toolSteps: m.metadata?.toolSteps?.map((s) => ({
              id: s.id,
              name: s.name,
              status: (s.status as ToolStep["status"]) ?? "done",
            })),
            guideOffers: m.metadata?.guideOffers,
          })),
        );
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, loaded, tenantId]);

  // Follow the newest content only while the user is near the bottom, so
  // scrolling up to read earlier output isn't interrupted mid-generation.
  const nearBottomRef = useRef(true);
  const handleScoutScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
  };
  // Opening the widget always jumps to the latest.
  useEffect(() => {
    if (open) nearBottomRef.current = true;
  }, [open]);
  useEffect(() => {
    if (!nearBottomRef.current) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streamText, streamSteps, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, streaming]);

  // "Discuss or fix this" on an eval finding drops the question into the box
  // rather than sending it, so the user can reword before spending a turn.
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string }>).detail;
      if (typeof detail?.message === "string") setInput(detail.message);
    };
    window.addEventListener("proclaw:auditor-prefill", onPrefill);
    return () => window.removeEventListener("proclaw:auditor-prefill", onPrefill);
  }, []);

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || streaming || !tenantId) return;
      setError(null);
      setInput("");
      setMessages((prev) => [...prev, { role: "user", content: message }]);
      setStreaming(true);
      setStreamText("");
      setStreamSteps([]);

      let accumulated = "";
      let steps: ToolStep[] = [];
      let offers: GuideOffer[] = [];
      const newProposalIds: string[] = [];
      try {
        const url =
          mode === "auditor"
            ? `/api/companies/${tenantId}/evals/chat/messages`
            : `/api/companies/${tenantId}/assistant/messages`;
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message,
            ...(mode === "auditor"
              ? { reportId: focusReportIdRef.current }
              : {
                  context: pageContextFromPath(location.pathname, location.search),
                  // Pin the turn to the conversation on screen. Without it a
                  // reply typed into a session reopened from history would be
                  // appended to whichever session was most recently touched.
                  ...(activeSessionId ? { sessionId: activeSessionId } : {}),
                }),
          }),
        });
        if (!res.ok || !res.body) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `Request failed (${res.status})`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let event: Record<string, unknown>;
            try {
              event = JSON.parse(line.slice(6));
            } catch {
              continue;
            }
            if (event.type === "text_delta" && typeof event.delta === "string") {
              accumulated += event.delta;
              setStreamText(accumulated);
            } else if (event.type === "tool_call") {
              const id = String(event.id ?? "");
              const status = event.status as ToolStep["status"];
              steps = steps.some((s) => s.id === id)
                ? steps.map((s) => (s.id === id ? { ...s, status } : s))
                : [...steps, { id, name: String(event.name ?? ""), status }];
              setStreamSteps(steps);
            } else if (event.type === "guide_offer") {
              offers = [
                ...offers,
                { guideId: String(event.guideId ?? ""), title: String(event.title ?? "") },
              ];
            } else if (event.type === "proposal") {
              // Note the id and re-read the row afterwards rather than
              // rendering from the event: the server row is the only source
              // of truth for status, and it carries the before/after diff the
              // reviewer actually decides on.
              newProposalIds.push(String(event.id ?? ""));
            } else if (event.type === "error") {
              throw new Error(String(event.error ?? "Assistant error"));
            }
          }
        }
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: accumulated || "(no response)",
            toolSteps: steps,
            guideOffers: offers,
          },
        ]);
        if (newProposalIds.length > 0 && tenantId) {
          const refreshed = await auditorChatApi.getSession(tenantId).catch(() => null);
          if (refreshed) setProposals(refreshed.proposals);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Something went wrong — please try again.");
        if (accumulated) {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: accumulated, toolSteps: steps, guideOffers: offers },
          ]);
        }
      } finally {
        setStreaming(false);
        setStreamText("");
        setStreamSteps([]);
      }
    },
    [streaming, tenantId, location.pathname, mode],
  );

  const reset = useCallback(() => {
    if (!tenantId || streaming) return;
    const call =
      mode === "auditor" ? auditorChatApi.reset(tenantId) : assistantApi.resetSession(tenantId);
    call
      .then(() => {
        setMessages([]);
        setProposals([]);
        setError(null);
      })
      .catch(() => undefined);
  }, [tenantId, streaming, mode]);

  /**
   * Switch which agent is behind the chat. Clears the transcript rather than
   * carrying it over — these are two separate stored conversations, and
   * showing Scout's history under the Auditor's name would misattribute
   * every earlier answer.
   */
  const switchMode = useCallback(
    (next: "scout" | "auditor") => {
      if (streaming || next === mode) return;
      setMode(next);
      setMessages([]);
      setProposals([]);
      setError(null);
      setLoaded(false);
      focusReportIdRef.current = null;
    },
    [mode, streaming],
  );

  const decideProposal = useCallback(
    async (proposalId: string, decision: "apply" | "dismiss") => {
      if (!tenantId) return;
      try {
        if (decision === "apply") await auditorChatApi.applyProposal(tenantId, proposalId);
        else await auditorChatApi.dismissProposal(tenantId, proposalId);
      } catch {
        // Swallow: the refresh below reports the real outcome from the row,
        // including the stale case where the target moved underneath it.
      }
      const refreshed = await auditorChatApi.getSession(tenantId).catch(() => null);
      if (refreshed) setProposals(refreshed.proposals);
    },
    [tenantId],
  );

  // Workflow guides need a workflow to look at. Same query and key as the
  // Workflows screen, so it is usually already cached. Above the early return:
  // a hook must run on every render.
  const { data: workflows = [] } = useQuery({
    queryKey: queryKeys.workflows.byCompany(tenantId),
    queryFn: () => workflowsApi.list(tenantId),
    enabled: !!tenantId && open,
  });

  if (tenantLoading || !tenantId) return null;

  // Guided walkthroughs the user's role permits, for the empty-state launcher.
  // Agent-dependent guides are hidden until the tenant has created at least
  // one agent (so "Create your first agent" comes first), and workflow guides
  // until there is a workflow to look at.
  const guideRole: GuideRole = isTenantAdmin
    ? "tenant_admin"
    : isAgentAdmin
      ? "agent_admin"
      : "standard_user";
  const hasAgent = agents.length > 0;
  const hasWorkflow = workflows.length > 0;
  const walkthroughs = UI_GUIDES.filter(
    (g) =>
      GUIDE_ROLE_LEVEL[guideRole] >= GUIDE_ROLE_LEVEL[g.requiredRole] &&
      !(g.requiresAgent && !hasAgent) &&
      !(g.requiresWorkflow && !hasWorkflow),
  );
  // Grouped under their headings; a heading with nothing for this person is left out.
  const walkthroughSections = GUIDE_SECTIONS.map((section) => ({
    ...section,
    guides: walkthroughs.filter((g) => g.section === section.id),
  })).filter((section) => section.guides.length > 0);

  return (
    <>
      {/* Launcher bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={`Open ${ASSISTANT_NAME}, the platform help assistant`}
          data-tour="scout"
          className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-white shadow-lg transition-transform hover:scale-105 hover:bg-accent-hover"
        >
          <HelpCircle size={22} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed z-40 flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
          style={{
            right: frame.right,
            bottom: frame.bottom,
            width: frame.width,
            height: frame.height,
          }}
        >
          {/* Resize grip, top-left. The panel is pinned bottom-right, so
              dragging this corner grows it without moving the far corner.
              Given a visible diagonal rather than an invisible hit area — an
              unmarked corner is a feature nobody finds. */}
          <div
            onPointerDown={startResize}
            role="separator"
            aria-label="Resize panel"
            title="Drag to resize"
            className="group absolute left-0 top-0 z-20 flex h-5 w-5 cursor-nwse-resize items-start justify-start p-1"
          >
            <svg
              viewBox="0 0 10 10"
              className="h-2.5 w-2.5 text-text-muted transition-colors group-hover:text-text-primary"
              aria-hidden="true"
            >
              <path
                d="M9 1 L1 9 M5 1 L1 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </div>
          {/* Header. Kept to one line at any panel width (2026-10-02): the
              subtitle is cut short rather than wrapped, and every icon button
              is the same 28px square. */}
          <div className="flex shrink-0 items-center gap-1 border-b border-border px-4 py-3">
            <div className="mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
              <Sparkles size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-semibold text-text-primary">
                {mode === "auditor" ? AUDITOR_NAME : ASSISTANT_NAME}
              </div>
              <div className="truncate text-[11.5px] text-text-muted">
                {view === "support"
                  ? "Support requests"
                  : mode === "auditor"
                    ? "Workflow & cost evaluation"
                    : "Platform help & troubleshooting"}
              </div>
            </div>
            {/* Recent conversations. Scout starts fresh each browser session,
                so this is how the user gets back to anything earlier. */}
            {/* Header buttons label themselves above, like the chat box's
                toolbar (ChatToolbarTooltip), drawn on the page so the panel's
                edge cannot cut the label off. */}
            {view === "chat" && mode === "scout" && (
              <ChatToolbarTooltip label="Recent conversations" portal className="shrink-0">
                <button
                  onClick={openHistory}
                  aria-label="Recent conversations"
                  aria-expanded={historyOpen}
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-surface-hover hover:text-text-primary",
                    historyOpen ? "bg-surface-hover text-text-primary" : "text-text-muted",
                  )}
                >
                  <HistoryIcon size={15} />
                </button>
              </ChatToolbarTooltip>
            )}
            {/* Agent switch — platform admins only. The Auditor reads
                cross-agent cost and config, so it is not offered to tenant
                users at all rather than offered and refused. */}
            {isInstanceAdmin && view === "chat" && (
              <div className="mx-1 flex shrink-0 rounded-md border border-border p-0.5">
                {(["scout", "auditor"] as const).map((m) => (
                  <ChatToolbarTooltip
                    key={m}
                    portal
                    label={
                      m === "scout"
                        ? "Platform help"
                        : "Evaluate workflows, agents and cost (Aztech staff only)"
                    }
                  >
                    <button
                      onClick={() => switchMode(m)}
                      disabled={streaming}
                      className={cn(
                        "rounded px-2 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-40",
                        mode === m
                          ? "bg-accent text-white"
                          : "text-text-muted hover:text-text-primary",
                      )}
                    >
                      {m === "scout" ? "Help" : "Audit"}
                    </button>
                  </ChatToolbarTooltip>
                ))}
              </div>
            )}
            {/* New conversation: a pen, the usual "new chat" icon. Shown only
                once there is a conversation to leave, never as a greyed-out
                button. */}
            {view === "chat" && messages.length > 0 && (
              <ChatToolbarTooltip label="Start a new conversation" portal className="shrink-0">
                <button
                  onClick={reset}
                  disabled={streaming}
                  aria-label="Start a new conversation"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary disabled:opacity-40"
                >
                  <SquarePen size={15} />
                </button>
              </ChatToolbarTooltip>
            )}
            {/* Support requests: one icon that lights up while open, like
                History, rather than turning into a different "back" icon. */}
            <ChatToolbarTooltip
              label={view === "chat" ? "Support requests" : "Back to chat"}
              portal
              className="shrink-0"
            >
              <button
                onClick={() => setView((v) => (v === "chat" ? "support" : "chat"))}
                data-tour={view === "chat" ? "scout-support" : undefined}
                aria-label="Support requests"
                aria-pressed={view === "support"}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-surface-hover hover:text-text-primary",
                  view === "support" ? "bg-surface-hover text-text-primary" : "text-text-muted",
                )}
              >
                <LifeBuoy size={15} />
              </button>
            </ChatToolbarTooltip>
            <ChatToolbarTooltip label="Close" portal className="shrink-0">
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
              >
                <X size={16} />
              </button>
            </ChatToolbarTooltip>
          </div>

          {/* Recent conversations — a panel-width sheet, not a dropdown.
              As a dropdown anchored to its header button it rendered past the
              panel's left edge and was clipped by the container's
              overflow-hidden: the panel can be as narrow as 320px, so nothing
              anchored to a header control is guaranteed to fit. Filling the
              body instead is width-independent, and gives the list room to be
              readable. */}
          {historyOpen && view === "chat" && mode === "scout" && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-2">
                <span className="text-[12px] font-medium text-text-primary">
                  Recent conversations
                </span>
                <button
                  onClick={startNewConversation}
                  className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11.5px] font-medium text-accent hover:bg-surface-hover"
                >
                  <Plus size={12} /> New
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto py-1">
                {sessions.length === 0 ? (
                  <p className="px-4 py-3 text-[11.5px] text-text-muted">
                    No earlier conversations yet.
                  </p>
                ) : (
                  sessions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => openSession(s.id)}
                      className={cn(
                        "block w-full px-4 py-2 text-left hover:bg-surface-hover",
                        s.id === activeSessionId && "bg-surface-hover",
                      )}
                    >
                      <span className="block truncate text-[12.5px] text-text-primary">
                        {s.preview?.trim() || s.title}
                      </span>
                      <span className="block text-[10.5px] text-text-muted">
                        {new Date(s.updatedAt).toLocaleDateString()} ·{" "}
                        {s.messageCount} message{s.messageCount === 1 ? "" : "s"}
                      </span>
                    </button>
                  ))
                )}
              </div>
              <button
                onClick={() => setHistoryOpen(false)}
                className="shrink-0 border-t border-border px-4 py-2 text-[11.5px] text-text-muted hover:text-text-primary"
              >
                Back to conversation
              </button>
            </div>
          )}

          {view === "support" ? (
            <SupportPanel tenantId={tenantId} initialTicketId={supportTicketId} />
          ) : historyOpen ? null : (
            <>
          {/* Messages */}
          <div ref={scrollRef} onScroll={handleScoutScroll} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
            {messages.length === 0 && !streaming && mode === "auditor" && (
              <div className="flex h-full flex-col justify-center gap-3">
                <p className="text-center text-[13px] text-text-secondary">
                  I&apos;m the {AUDITOR_NAME}. Ask me why a workflow is failing, what it costs, or
                  whether a cheaper model would cope — and I can draft changes for you to confirm.
                </p>
                <div className="flex flex-col gap-1.5">
                  {AUDITOR_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => void send(s)}
                      className="rounded-lg border border-border bg-background px-3 py-2 text-left text-[12.5px] text-text-secondary hover:bg-surface-hover hover:text-text-primary"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.length === 0 && !streaming && mode === "scout" && (
              <div className="flex flex-col gap-3 py-1">
                <p className="text-center text-[13px] text-text-secondary">
                  Hi, I&apos;m {ASSISTANT_NAME}. Ask me how the platform works, or ask me to look
                  into an agent or workflow that isn&apos;t behaving.
                </p>
                <div className="flex flex-col gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => void send(s)}
                      className="rounded-lg border border-border bg-background px-3 py-2 text-left text-[12.5px] text-text-secondary hover:bg-surface-hover hover:text-text-primary"
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {walkthroughs.length > 0 && (
                  <div>
                    <p className="mb-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                      Guided walkthroughs
                    </p>
                    {walkthroughSections.map((section, sectionIndex) => (
                      <div key={section.id} className={sectionIndex > 0 ? "mt-3" : undefined}>
                        <p className="mb-1 px-0.5 text-[11.5px] font-medium text-text-secondary">{section.label}</p>
                        <div className="flex flex-col gap-1.5">
                          {section.guides.map((g) => (
                            <button
                              key={g.id}
                              onClick={() => {
                                setOpen(false);
                                window.dispatchEvent(
                                  new CustomEvent("proclaw:start-guide", {
                                    detail: { guideId: g.id },
                                  }),
                                );
                              }}
                              className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left hover:bg-surface-hover"
                            >
                              <Sparkles size={13} className="shrink-0 text-accent" />
                              <span className="text-[12.5px] font-medium text-text-primary">
                                {g.title}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-accent/10 px-3.5 py-2 text-[13px] text-text-primary">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={i} className="space-y-1.5">
                  {m.toolSteps && m.toolSteps.length > 0 && (
                    <div className="space-y-1">
                      {m.toolSteps.map((s) => (
                        <ToolStepRow key={s.id} step={{ ...s, status: s.status === "running" ? "done" : s.status }} />
                      ))}
                    </div>
                  )}
                  <div className="text-[13px] text-text-primary">
                    <MarkdownRenderer content={m.content} />
                  </div>
                  {m.guideOffers && m.guideOffers.length > 0 && (
                    <div className="flex flex-col gap-1.5 pt-1">
                      {m.guideOffers.map((g) => (
                        <button
                          key={g.guideId}
                          onClick={() => {
                            setOpen(false);
                            window.dispatchEvent(
                              new CustomEvent("proclaw:start-guide", {
                                detail: { guideId: g.guideId },
                              }),
                            );
                          }}
                          className="flex items-center gap-2 self-start rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-[12.5px] font-medium text-accent hover:bg-accent/10"
                        >
                          <Sparkles size={13} />
                          Show me — {g.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ),
            )}

            {/* Drafted changes sit below the transcript rather than inline,
                so a decision the user hasn't made yet stays visible instead
                of scrolling away as the conversation continues. */}
            {mode === "auditor" && proposals.length > 0 && (
              <div className="space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  Proposed changes
                </div>
                {proposals.map((p) => (
                  <ProposalCard key={p.id} proposal={p} onDecide={decideProposal} />
                ))}
              </div>
            )}

            {streaming && (
              <div className="space-y-1.5">
                {streamSteps.length > 0 && (
                  <div className="space-y-1">
                    {streamSteps.map((s) => (
                      <ToolStepRow key={s.id} step={s} />
                    ))}
                  </div>
                )}
                {streamText ? (
                  <div className="text-[13px] text-text-primary">
                    <MarkdownRenderer content={streamText} />
                  </div>
                ) : streamSteps.length === 0 ? (
                  <div className="flex items-center gap-2 text-[12.5px] text-text-muted">
                    <Loader2 size={13} className="animate-spin" /> Thinking…
                  </div>
                ) : null}
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-[12.5px] text-danger">
                {error}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="shrink-0 border-t border-border p-3">
            <div className="flex items-end gap-2 rounded-xl border border-border bg-background px-3 py-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send(input);
                  }
                }}
                placeholder={`Ask ${ASSISTANT_NAME} anything…`}
                rows={1}
                disabled={streaming}
                className="max-h-24 min-h-[22px] flex-1 resize-none bg-transparent text-[13px] text-text-primary outline-none placeholder:text-text-muted disabled:opacity-60"
              />
              <button
                onClick={() => void send(input)}
                disabled={streaming || !input.trim()}
                aria-label="Send"
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors",
                  input.trim() && !streaming
                    ? "bg-accent text-white hover:bg-accent-hover"
                    : "bg-surface-hover text-text-muted",
                )}
              >
                {streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
